import { useEffect, useState } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import Chip from '../chip';
import useUserMemory from '../../hooks/use-user-memory';
import { reviewService } from '../../queries/review';
import { CardBack, CardFront } from '../card-review';
import { Modal } from '../modal';
import { flashcardService } from '../../queries';
import { Icon } from '../../../../core/components/atoms/Icon';
import { useReviewStore } from '../../stores/review';

export default function QuickFlashcardReview({
  isOpen,
  onClose,
  flashcardUuid,
  onSuccessFallback,
  subject,
}: {
  isOpen: boolean;
  onClose: VoidFunction;
  flashcardUuid: string;
  onSuccessFallback?: (value?: boolean) => void;
  subject?: {
    id: string;
    name: string;
  };
}) {
  const { setFlashcards } = useReviewStore();
  const [isLoading, setIsLoading] = useState(false);
  const { memoryUserUuid } = useUserMemory();

  const [flashcard, setFlashcard] = useState<any | null>(null);
  const [isModalSuccessAddToDeck, setIsModalSuccessAddToDeck] = useState(false);

  const [difficulty, setDifficulty] = useState<
    'easy' | 'medium' | 'hard' | null
  >(null);
  const [flipped, setFlipped] = useState(false);

  const handleChangeDifficulty = async (
    difficulty: 'easy' | 'medium' | 'hard'
  ) => {
    if (isLoading) return;

    setIsLoading(true);
    setDifficulty(difficulty);

    await handleSaveAnswer(difficulty);
  };

  const handleSaveAnswer = async (difficulty: 'easy' | 'medium' | 'hard') => {
    if (!memoryUserUuid) return;

    try {
      await reviewService.saveAnswerByFlashcardUuid(
        memoryUserUuid,
        flashcard.uuid,
        difficulty,
        subject
      );
      setDifficulty(null);
      setIsLoading(false);
      setIsModalSuccessAddToDeck(true);
    } catch (err) {
      setIsLoading(false);
      console.log('err', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFlipCard = (flipped: boolean) => {
    setFlipped(flipped);
  };

  const onCloseAllModal = () => {
    setIsModalSuccessAddToDeck(false);
    setDifficulty(null);
    setFlipped(false);
    setFlashcard(null);
    onClose?.();

    onSuccessFallback?.();
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchFlashcard = async () => {
      if (!memoryUserUuid || !flashcardUuid) return;

      const data = await flashcardService.get(memoryUserUuid, flashcardUuid);
      setFlashcard(data);
    };

    fetchFlashcard();
  }, [flashcardUuid, memoryUserUuid, isOpen, setFlashcards]);

  const frontText = flashcard?.front || 'Carregando...';
  const backText = flashcard?.back || 'Carregando...';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <i className='font-bold text-[#2E3847] dark:text-[#FFF]'>Flashcard</i>
      }
    >
      <div
        className={`flex w-full max-w-[748px] flex-col items-center justify-center gap-5`}
      >
        <div className='flex w-full flex-col'>
          <Typography className={`text-h4 font-bold text-mono-color-darkest `}>
            {flipped ? 'Verso' : 'Frente'}
          </Typography>
        </div>
        {flipped && (
          <div className='flex w-full flex-col items-center justify-center'>
            <Typography className='w-full truncate text-center text-sm font-bold text-mono-color-dark'>
              {frontText}
            </Typography>
          </div>
        )}
        <div className='relative h-48 w-full [perspective:1000px]'>
          <div
            className={`relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d] ${
              flipped ? '[transform:rotateY(180deg)]' : ''
            }`}
          >
            <CardFront
              setFlipped={handleFlipCard}
              text={frontText}
              isActive={!flipped}
            />
            <CardBack
              setFlipped={handleFlipCard}
              text={backText}
              isActive={flipped}
            />
          </div>
        </div>

        {flipped && (
          <>
            <div className='flex w-full flex-row gap-4'>
              <Chip
                label='Fácil'
                defaultIcon
                className='w-full'
                variant='easy'
                onClick={() => handleChangeDifficulty('easy')}
                type={difficulty === 'easy' ? 'filled' : 'outline'}
                disabled={isLoading}
              />
              <Chip
                label='Médio'
                defaultIcon
                className='w-full'
                variant='medium'
                onClick={() => handleChangeDifficulty('medium')}
                type={difficulty === 'medium' ? 'filled' : 'outline'}
                disabled={isLoading}
              />
              <Chip
                label='Difícil'
                defaultIcon
                className='w-full'
                variant='hard'
                onClick={() => handleChangeDifficulty('hard')}
                type={difficulty === 'hard' ? 'filled' : 'outline'}
                disabled={isLoading}
              />
            </div>
          </>
        )}
        <div className='flex w-full flex-row'>
          <button
            className={`w-full rounded-md py-2 ${
              flipped
                ? 'border-none bg-color-brand-primary'
                : 'border-thin border-color-medium'
            }`}
            onClick={onClose}
            disabled={isLoading}
          >
            <Typography
              className={`text-h4 font-bold text-mono-color-darkest ${
                flipped ? 'text-[#FFF]' : ''
              }`}
            >
              {isLoading ? (
                <Icon
                  type='material'
                  Icon='progress_activity'
                  className='animate-spin'
                />
              ) : flipped ? (
                'Fechar'
              ) : (
                'Cancelar'
              )}
            </Typography>
          </button>
        </div>
      </div>
      {isModalSuccessAddToDeck && (
        <ModalSuccessAddToDeck
          isOpen={isModalSuccessAddToDeck}
          onClose={onCloseAllModal}
        />
      )}
    </Modal>
  );
}

const ModalSuccessAddToDeck = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: VoidFunction;
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <i className='font-bold text-[#2E3847] dark:text-[#FFF]'>Flashcard</i>
      }
      footer={
        <div className='flex w-full flex-row'>
          <button
            className='w-full rounded-md border-none bg-color-brand-primary py-2'
            onClick={onClose}
          >
            <Typography className='text-h4 font-bold text-[#FFF]'>
              Fechar
            </Typography>
          </button>
        </div>
      }
    >
      <div className='flex w-full flex-col justify-center gap-2'>
        <Typography className='font-bold'>
          Este flashcard foi salvo nos Flashcards.
        </Typography>
        <Typography>
          Ele entrará no seu ciclo de revisão. Você poderá acessá-lo quando o
          sistema indicar o melhor momento.
        </Typography>
      </div>
    </Modal>
  );
};
